import { Avatar, Divider } from "@mui/material";
import { motion } from "motion/react";
import { TbPlaneArrival, TbPlaneDeparture, TbUser, TbUserStar } from "react-icons/tb";

import ClipBoard from "@/components/ClipBoard";
import { softColors } from "@/constants/colors";
import { capitalizeWords } from "@/helpers/strings";
import {
    PassengerTypeColumnCell,
    PassengerTypeColumnCellSkeleton,
} from "@/pages/Plannn/components/table/PassengerTypeColumn";
import { PassengerTypes } from "@/types/global";
import { IResPassengersSearchResult } from "@/types/tasks.types";

import { PassengerCardSkeleton } from "../PassengerCard";
import GlobalSearchPassengerDirectionItem, {
    GlobalSearchPassengerDirectionItemSkeleton,
} from "./GlobalSearchPassengerDirectionItem";
import HighlightText from "./HighlightText";

const classes = {
    wrapper: "shadow border rounded-lg flex-col flex relative overflow-hidden",
    headerClass: "border-b px-4 py-1 grow-0 shrink-0 flex justify-between items-center",
    contentClass: "grid grid-grid-cols-1 md:grid-cols-[1fr,10px,1fr] pt-2 md:pt-0 gap-4 px-4 grow",
};
export function GlobalSearchPassengerItemSkeleton() {
    return (
        <motion.div className={classes.wrapper}>
            <div className={classes.headerClass}>
                <PassengerCardSkeleton className="gap-2" />
                <PassengerTypeColumnCellSkeleton />
            </div>
            <div className={classes.contentClass}>
                <GlobalSearchPassengerDirectionItemSkeleton />
                <Divider orientation="vertical" className=" !h-1/2 !my-auto" />
                <GlobalSearchPassengerDirectionItemSkeleton />
            </div>
        </motion.div>
    );
}

function GlobalSearchPassengerItem({
    item: { passengerType, name, arrival, departure },
    highlightName,
    highlightFlightNumber,
}: {
    item: IResPassengersSearchResult;
    highlightName?: string;
    highlightFlightNumber?: string;
}) {
    const isCustom = passengerType === PassengerTypes.MANUAL;
    const avatarStyle = {
        width: 25,
        height: 25,
        backgroundColor: isCustom ? softColors.indigo.bg : "default",
        color: isCustom ? softColors.indigo.hover : "default",
    };

    return (
        <motion.div
            className={classes.wrapper}
            whileHover={{
                scale: 1.01,
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                transition: { duration: 0.2 },
                borderColor: softColors.indigo.hover,
            }}
            whileTap={{
                scale: 0.99,
                transition: { duration: 0.2 },
            }}
        >
            <div className={classes.headerClass}>
                <div className="flex items-center h-full hover:underline gap-2">
                    <Avatar sx={avatarStyle} className="!ml-1" variant="rounded">
                        {isCustom ? <TbUserStar size={16} /> : <TbUser size={16} />}
                    </Avatar>
                    <div className="font-semibold !capitalize break-all text-gray-900">
                        <ClipBoard text={capitalizeWords(name || "")}>
                            {highlightName ? <HighlightText text={name} highlight={highlightName} /> : name}
                        </ClipBoard>
                    </div>
                </div>
                <PassengerTypeColumnCell className="!h-fit" value={passengerType} />
            </div>
            <div className={classes.contentClass}>
                <GlobalSearchPassengerDirectionItem
                    color={softColors.indigo}
                    titleIcon={TbPlaneArrival}
                    title="Geliş Bilgileri"
                    passenger={arrival}
                    highlightFlightNumber={highlightFlightNumber}
                />
                <Divider orientation="vertical" className="hidden md:!block !h-1/2 !my-auto" />
                <GlobalSearchPassengerDirectionItem
                    color={softColors.green}
                    titleIcon={TbPlaneDeparture}
                    title="Gidiş Bilgileri"
                    passenger={departure}
                    highlightFlightNumber={highlightFlightNumber}
                />
            </div>
        </motion.div>
    );
}

export default GlobalSearchPassengerItem;
